import { useState } from "react";
import type { MsdsGenerateHints } from "@/lib/manufacturing/msdsDocumentTypes";
import {
  inputClass,
  mfgPrimaryBtnClass,
  mfgSecondaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";

const HAZARD_OPTIONS = [
  "flammable",
  "corrosive",
  "irritant",
  "sensitizer",
  "toxic",
  "oxidizer",
] as const;

type Props = {
  generating: boolean;
  disabled: boolean;
  onGenerate: (hints: MsdsGenerateHints) => void;
};

/** MSDS-4 — optional chemical hints + Generate draft. */
export function ManufacturingMaterialSdsGenerateForm({
  generating,
  disabled,
  onGenerate,
}: Props) {
  const [open, setOpen] = useState(false);
  const [chemicalName, setChemicalName] = useState("");
  const [casNumbers, setCasNumbers] = useState("");
  const [manufacturer, setManufacturer] = useState("");
  const [intendedUse, setIntendedUse] = useState("");
  const [compositionNotes, setCompositionNotes] = useState("");
  const [hazards, setHazards] = useState<string[]>([]);

  function toggleHazard(h: string) {
    setHazards((prev) => (prev.includes(h) ? prev.filter((x) => x !== h) : [...prev, h]));
  }

  function submit() {
    onGenerate({
      chemical_name: chemicalName.trim() || undefined,
      cas_numbers: casNumbers.trim() || undefined,
      manufacturer: manufacturer.trim() || undefined,
      intended_use: intendedUse.trim() || undefined,
      composition_notes: compositionNotes.trim() || undefined,
      hazard_classes: hazards.length ? hazards : undefined,
    });
  }

  return (
    <div className="rounded-lg border border-gray-200 p-3 dark:border-zinc-700">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-xs font-medium text-gray-800 dark:text-zinc-100">
            Generate draft with AI
          </div>
          <p className="text-[11px] text-gray-500 dark:text-zinc-400">
            Optional hints improve quality. Overwrites the current draft sections.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className={mfgSecondaryBtnClass}
            onClick={() => setOpen((v) => !v)}
            disabled={disabled || generating}
          >
            {open ? "Hide hints" : "Hints"}
          </button>
          <button
            type="button"
            className={mfgPrimaryBtnClass}
            onClick={submit}
            disabled={disabled || generating}
          >
            {generating ? "Generating…" : "Generate draft"}
          </button>
        </div>
      </div>
      {open ? (
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
          <label className="block text-[11px] text-gray-600 dark:text-zinc-300">
            Chemical name / synonyms
            <input
              className={`${inputClass} mt-0.5`}
              value={chemicalName}
              onChange={(e) => setChemicalName(e.target.value)}
              disabled={generating}
            />
          </label>
          <label className="block text-[11px] text-gray-600 dark:text-zinc-300">
            CAS number(s)
            <input
              className={`${inputClass} mt-0.5`}
              value={casNumbers}
              onChange={(e) => setCasNumbers(e.target.value)}
              disabled={generating}
            />
          </label>
          <label className="block text-[11px] text-gray-600 dark:text-zinc-300">
            Manufacturer / distributor
            <input
              className={`${inputClass} mt-0.5`}
              value={manufacturer}
              onChange={(e) => setManufacturer(e.target.value)}
              disabled={generating}
            />
          </label>
          <label className="block text-[11px] text-gray-600 dark:text-zinc-300">
            Intended use at plant
            <input
              className={`${inputClass} mt-0.5`}
              value={intendedUse}
              onChange={(e) => setIntendedUse(e.target.value)}
              disabled={generating}
            />
          </label>
          <label className="block text-[11px] text-gray-600 dark:text-zinc-300 sm:col-span-2">
            Composition notes
            <textarea
              className={`${inputClass} mt-0.5 min-h-[56px]`}
              value={compositionNotes}
              onChange={(e) => setCompositionNotes(e.target.value)}
              disabled={generating}
            />
          </label>
          <div className="sm:col-span-2">
            <div className="mb-1 text-[11px] text-gray-600 dark:text-zinc-300">
              Known hazard classes
            </div>
            <div className="flex flex-wrap gap-2">
              {HAZARD_OPTIONS.map((h) => (
                <label key={h} className="inline-flex items-center gap-1 text-[11px]">
                  <input
                    type="checkbox"
                    checked={hazards.includes(h)}
                    onChange={() => toggleHazard(h)}
                    disabled={generating}
                  />
                  {h}
                </label>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
